import { type FC, type ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { type PdpSimilarItemsSponsoredMetadata } from './mergePdpSimilarItemsAndSponsoredItems.ts';
import { isEligibleForSoldPdpSponsoredYmalGrid } from './isEligibleForSoldPdpSponsoredYmalGrid.ts';
import {
    type GridSponsoredItem,
    usePdpRespSimilarItemsSponsoredLoadMore,
} from './usePdpRespSimilarItemsSponsoredLoadMore.ts';
import {
    SOLD_PDP_YMAL_MAX_GRID_SPONSORED,
    SOLD_PDP_YMAL_SL_INITIAL,
    SOLD_PDP_YMAL_ITEMS_PER_ROW,
} from './pdpRespSimilarItemsConstants.ts';

import { type PdpRespSimilarSponsoredItemsProvider_viewer$key } from './__generated__/PdpRespSimilarSponsoredItemsProvider_viewer.graphql.ts';

type PdpSponsoredCarouselCounts = {
    eligibleItemIdsCount: number;
    inflatedItemsCount: number;
};

type PdpRespSimilarSponsoredItemsContextType = {
    /** AB variant; false until afterDisplay viewer is passed. */
    isSoldPdpSponsoredYmalGridVariant: boolean;
    /** AB variant + desktop + ≥1 full grid SL row. */
    isEffectiveVariant: boolean;
    /** Enough SLs for AB exposure tracking (both arms). */
    isEligibleForAbTracking: boolean;
    /** Called by the featured carousel once it knows its SL pool sizes (both AB arms). */
    registerCarouselSponsoredCounts: (counts: PdpSponsoredCarouselCounts) => void;
    gridSponsoredItems: readonly GridSponsoredItem[];
    /** Inflated SLs past the grid cap — empty means hide featured carousel. */
    featuredCarouselSponsoredItems: readonly GridSponsoredItem[];
    metadataByItemId: Record<string, PdpSimilarItemsSponsoredMetadata>;
    sponsoredPagesLoaded: number;
    hasNextSponsored: boolean;
    isLoadingNextSponsored: boolean;
    hasSponsoredLoadError: boolean;
    inflateNextSponsored: (count?: number) => Promise<void>;
    isSponsoredListingsLoaded: boolean;
    gridSponsoredListingsIdsCount: number;
};

const emptyContext: PdpRespSimilarSponsoredItemsContextType = {
    isSoldPdpSponsoredYmalGridVariant: false,
    isEffectiveVariant: false,
    isEligibleForAbTracking: false,
    registerCarouselSponsoredCounts: () => {},
    gridSponsoredItems: [],
    featuredCarouselSponsoredItems: [],
    metadataByItemId: {},
    sponsoredPagesLoaded: 0,
    hasNextSponsored: false,
    isLoadingNextSponsored: false,
    hasSponsoredLoadError: false,
    inflateNextSponsored: () => Promise.resolve(),
    isSponsoredListingsLoaded: false,
    gridSponsoredListingsIdsCount: 0,
};

const PdpRespSimilarSponsoredItemsContext =
    createContext<PdpRespSimilarSponsoredItemsContextType>(emptyContext);

export const usePdpRespSimilarSponsoredItemsContext = (): PdpRespSimilarSponsoredItemsContextType =>
    useContext(PdpRespSimilarSponsoredItemsContext);

const viewerFragment = graphql`
    fragment PdpRespSimilarSponsoredItemsProvider_viewer on Viewer {
        sponsoredItems(
            userId: $userId
            guestId: $guestId
            itemId: $itemId
            pageSize: $numOfSponsoredItems
            maxItems: $maxSponsoredItems
            minItemsSteps: $minSponsoredItemsSteps
        ) {
            eligibleItemIds
            metadata {
                itemId
                impressionTrackerLink
                clickTrackerLink
            }
            items {
                serviceId
                title
                browseUrl
                categoryCode
                ecommerceTrackingParams
                ...PdpRespSimilarItemsGrid_similarItems
                ...SharedRespRelatedItem_item @arguments(priceBookName: $priceBookName)
                ...FavoritesProvider_item
            }
        }
    }
`;

export const PdpRespSimilarSponsoredItemsProvider: FC<{
    viewer: PdpRespSimilarSponsoredItemsProvider_viewer$key | null | undefined;
    isTrade: boolean;
    priceBookName?: string;
    children: ReactNode;
}> = ({ viewer: viewerRef, isTrade, priceBookName = '', children }) => {
    const { soldPdpSponsoredYmalGridTest } = useServerVarsContext();
    const isSoldPdpSponsoredYmalGridVariant = soldPdpSponsoredYmalGridTest === 'variant';
    const viewer = useFragment(
        viewerFragment,
        isSoldPdpSponsoredYmalGridVariant ? viewerRef : null
    );

    const sponsoredItems = viewer?.sponsoredItems?.items;
    const sponsoredMetadata = viewer?.sponsoredItems?.metadata;

    const eligibleItemIds = useMemo(
        () => (viewer?.sponsoredItems?.eligibleItemIds || []).filter(filterFalsy),
        [viewer?.sponsoredItems?.eligibleItemIds]
    );

    // Control never fetches the provider fragment, so the featured carousel (fetched in both
    // arms) reports its SL pool sizes; the provider owns the eligibility rule so it lives in
    // one place.
    const [carouselCounts, setCarouselCounts] = useState<PdpSponsoredCarouselCounts | null>(null);
    const isEligibleForAbTracking =
        !!carouselCounts &&
        carouselCounts.inflatedItemsCount > 0 &&
        isEligibleForSoldPdpSponsoredYmalGrid(carouselCounts);

    const isEffectiveVariant =
        isSoldPdpSponsoredYmalGridVariant &&
        isEligibleForSoldPdpSponsoredYmalGrid({
            eligibleItemIdsCount: eligibleItemIds.length,
            inflatedItemsCount: (sponsoredItems || []).filter(filterFalsy).length,
        });

    const {
        extraGridItems,
        carouselLeftoverItems,
        sponsoredPagesLoaded,
        hasNextSponsored: hasNextSponsoredFromPool,
        isLoadingNextSponsored,
        hasSponsoredLoadError,
        inflateNextSponsored,
    } = usePdpRespSimilarItemsSponsoredLoadMore({
        enabled: isEffectiveVariant,
        eligibleItemIds,
        isTrade,
        priceBookName,
        // Initial query inflates SOLD_PDP_YMAL_SL_INITIAL; remaining pool IDs load with YMAL pages.
        initialOffset: SOLD_PDP_YMAL_SL_INITIAL,
    });

    const value = useMemo((): PdpRespSimilarSponsoredItemsContextType => {
        const initialItems = (sponsoredItems || []).filter(filterFalsy);
        const allInflatedSponsoredItems = isEffectiveVariant
            ? ([...initialItems, ...extraGridItems] as GridSponsoredItem[])
            : [];
        const gridSponsoredItems = allInflatedSponsoredItems.slice(
            0,
            SOLD_PDP_YMAL_MAX_GRID_SPONSORED
        );
        const featuredCarouselSponsoredItems = isEffectiveVariant ? carouselLeftoverItems : [];
        const metadataByItemId = !isEffectiveVariant
            ? {}
            : (sponsoredMetadata || [])
                  .filter(filterFalsy)
                  .reduce<Record<string, PdpSimilarItemsSponsoredMetadata>>((acc, meta) => {
                      if (meta.itemId) {
                          acc[meta.itemId] = {
                              impressionTrackerLink: meta.impressionTrackerLink,
                              clickTrackerLink: meta.clickTrackerLink,
                          };
                      }
                      return acc;
                  }, {});
        const shouldAwaitSponsoredListings = isSoldPdpSponsoredYmalGridVariant;
        const cappedCount = eligibleItemIds.slice(0, SOLD_PDP_YMAL_MAX_GRID_SPONSORED).length;
        const gridSponsoredListingsIdsCount =
            cappedCount - (cappedCount % SOLD_PDP_YMAL_ITEMS_PER_ROW);

        return {
            isSoldPdpSponsoredYmalGridVariant,
            isEffectiveVariant,
            isEligibleForAbTracking,
            registerCarouselSponsoredCounts: setCarouselCounts,
            gridSponsoredItems,
            featuredCarouselSponsoredItems,
            metadataByItemId,
            sponsoredPagesLoaded,
            hasNextSponsored:
                hasNextSponsoredFromPool &&
                gridSponsoredItems.length < SOLD_PDP_YMAL_MAX_GRID_SPONSORED,
            isLoadingNextSponsored,
            hasSponsoredLoadError,
            inflateNextSponsored,
            isSponsoredListingsLoaded: !shouldAwaitSponsoredListings || viewer !== null,
            gridSponsoredListingsIdsCount,
        };
    }, [
        extraGridItems,
        carouselLeftoverItems,
        hasNextSponsoredFromPool,
        hasSponsoredLoadError,
        isEffectiveVariant,
        isEligibleForAbTracking,
        isLoadingNextSponsored,
        isSoldPdpSponsoredYmalGridVariant,
        inflateNextSponsored,
        sponsoredItems,
        sponsoredMetadata,
        sponsoredPagesLoaded,
        eligibleItemIds,
        viewer,
    ]);

    return (
        <PdpRespSimilarSponsoredItemsContext value={value}>
            {children}
        </PdpRespSimilarSponsoredItemsContext>
    );
};
